import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '@/config/api';

export default function ChangePassword() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isForced = user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (form.new_password !== form.confirm_password) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, );
      toast.success('Password changed successfully!');
      await checkAuth();
      navigate(user?.role === 'admin' ? '/admin' : '/recruiter');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 bg-gray-50" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full mb-4">
            <Lock size={28} className="text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            {isForced ? 'Set Your New Password' : 'Change Password'}
          </h1>
          <p className="text-gray-500 mt-2 text-sm">
            {isForced
              ? 'For security, please change your temporary password before accessing the portal.'
              : 'Enter your current password and choose a new one.'
            }
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-form">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="password-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="current">Current Password</Label>
              <Input
                id="current"
                type="password"
                value={form.current_password}
                onChange={e => setForm({ ...form, current_password: e.target.value })}
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New Password</Label>
              <Input
                id="new"
                type="password"
                value={form.new_password}
                onChange={e => setForm({ ...form, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm New Password</Label>
              <Input
                id="confirm"
                type="password"
                value={form.confirm_password}
                onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-800 hover:bg-blue-700 text-white h-11" data-testid="change-password-submit-btn">
              <ShieldCheck size={18} className="mr-2" />
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
